// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  pages/Configuracoes/EmitenteFiscalTab.tsx
//
//  Aba "Emitente" de Configurações Fiscais: cadastra a empresa no serviço de
//  emissão (regime, ambiente, séries, CSC), envia o certificado A1 e testa a
//  comunicação com a SEFAZ. Os dados cadastrais vêm do cadastro da empresa.
// ════════════════════════════════════════════════════════════════════════════

import { useEffect, useRef, useState } from 'react';
import {
  AlertTriangle, Building2, CheckCircle2, FileKey, Info, Radio, Save, ShieldCheck, Upload,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useEmitenteFiscal } from '@/features/configuracoes/hooks/useConfiguracoes';
import type { AmbienteFiscal, SalvarEmitenteFiscalRequest } from '@/features/configuracoes/types';

const CRT_LABELS: Record<number, string> = {
  1: '1 — Simples Nacional',
  2: '2 — Simples Nacional (excesso de sublimite)',
  3: '3 — Regime Normal (Lucro Presumido / Real)',
  4: '4 — MEI',
};

function Campo({ rotulo, valor }: { rotulo: string; valor: string | number | null | undefined }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{rotulo}</p>
      <p className="text-sm font-medium">{valor || '—'}</p>
    </div>
  );
}

export function EmitenteFiscalTab({ empresaId }: { empresaId: number }) {
  const {
    data, isLoading, salvar, isSalvando, enviarCertificado, isEnviandoCertificado,
    testarSefaz, isTestandoSefaz, statusSefaz,
  } = useEmitenteFiscal(empresaId);

  const [form, setForm] = useState<SalvarEmitenteFiscalRequest>({
    crt: 1, ambiente: 'homologacao', serieNfe: 1, serieNfce: 1, cscNfce: null, idTokenNfce: null,
    percCreditoIcmsSn: 0,
  });
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [senha, setSenha] = useState('');
  const inputArquivo = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (data) setForm({
      crt: data.crt, ambiente: data.ambiente, serieNfe: data.serieNfe, serieNfce: data.serieNfce,
      cscNfce: null, idTokenNfce: data.idTokenNfce, percCreditoIcmsSn: data.percCreditoIcmsSn,
    });
  }, [data]);

  if (isLoading || !data) {
    return (
      <div className="pt-4 space-y-3">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  const { cadastro, certificado, pendencias } = data;
  const bloqueado = !data.servicoDisponivel || pendencias.length > 0;

  const enviar = () => {
    if (!arquivo) return;
    enviarCertificado({ arquivo, senha }, {
      onSuccess: () => {
        setArquivo(null);
        setSenha('');
        if (inputArquivo.current) inputArquivo.current.value = '';
      },
    });
  };

  return (
    <div className="pt-4 space-y-4">
      {!data.servicoDisponivel && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Serviço de emissão não configurado</AlertTitle>
          <AlertDescription>
            Este ambiente não está ligado ao serviço de emissão fiscal. Fale com o suporte.
          </AlertDescription>
        </Alert>
      )}

      {pendencias.length > 0 && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Cadastro da empresa incompleto</AlertTitle>
          <AlertDescription>
            Complete em Cadastros → Empresa antes de configurar a emissão: {pendencias.join(', ')}.
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Building2 className="h-4 w-4 text-primary" /> Dados do emitente
          </CardTitle>
          <CardDescription>
            Vêm do cadastro da empresa e são os que saem na nota. Para alterar, edite o cadastro da empresa.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Campo rotulo="CNPJ" valor={cadastro.cnpj} />
          <Campo rotulo="Razão social" valor={cadastro.razaoSocial} />
          <Campo rotulo="Inscrição estadual" valor={cadastro.inscricaoEstadual} />
          <Campo rotulo="Município / UF" valor={[cadastro.nomeCidade, cadastro.uf].filter(Boolean).join(' / ')} />
          <Campo rotulo="Código IBGE" valor={cadastro.codigoIbgeCidade} />
          <Campo rotulo="Endereço" valor={[cadastro.endereco, cadastro.numero].filter(Boolean).join(', ')} />
          <Campo rotulo="Bairro" valor={cadastro.bairro} />
          <Campo rotulo="CEP" valor={cadastro.cep} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Radio className="h-4 w-4 text-primary" /> Emissão
            {data.cadastrado
              ? <Badge variant="secondary">Cadastrado</Badge>
              : <Badge variant="outline">Ainda não cadastrado</Badge>}
          </CardTitle>
          <CardDescription>Regime, ambiente e numeração usados na NF-e e na NFC-e.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Regime tributário (CRT)</Label>
              <Select
                value={String(form.crt)}
                onValueChange={(v) => setForm((f) => ({ ...f, crt: Number(v) }))}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(CRT_LABELS).map(([valor, rotulo]) => (
                    <SelectItem key={valor} value={valor}>{rotulo}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Ambiente</Label>
              <Select
                value={form.ambiente}
                onValueChange={(v) => setForm((f) => ({ ...f, ambiente: v as AmbienteFiscal }))}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="homologacao">Homologação (sem valor fiscal)</SelectItem>
                  <SelectItem value="producao">Produção</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {form.crt !== 3 && (
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="cred-sn">Crédito de ICMS do Simples (%)</Label>
                <Input
                  id="cred-sn" type="number" min={0} max={10} step={0.01} value={form.percCreditoIcmsSn}
                  onChange={(e) => setForm((f) => ({ ...f, percCreditoIcmsSn: Number(e.target.value) || 0 }))}
                />
                <p className="text-xs text-muted-foreground">
                  Percentual que o cliente pode aproveitar (art. 23 da LC 123/2006), destacado nas notas com
                  CSOSN 101, 201 e 900. Muda com a faixa de faturamento — confirme com o contador. Zero não destaca.
                </p>
              </div>
            </div>
          )}

          {form.ambiente === 'producao' && (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertTitle>Ambiente de produção</AlertTitle>
              <AlertDescription>
                As notas emitidas terão valor fiscal. Emita ao menos uma nota em homologação antes de trocar.
              </AlertDescription>
            </Alert>
          )}

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-2">
              <Label htmlFor="serie-nfe">Série da NF-e</Label>
              <Input
                id="serie-nfe" type="number" min={1} max={889} value={form.serieNfe}
                onChange={(e) => setForm((f) => ({ ...f, serieNfe: Number(e.target.value) || 1 }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="serie-nfce">Série da NFC-e</Label>
              <Input
                id="serie-nfce" type="number" min={1} max={889} value={form.serieNfce}
                onChange={(e) => setForm((f) => ({ ...f, serieNfce: Number(e.target.value) || 1 }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="id-token">ID do token (NFC-e)</Label>
              <Input
                id="id-token" value={form.idTokenNfce ?? ''} placeholder="000001"
                onChange={(e) => setForm((f) => ({ ...f, idTokenNfce: e.target.value || null }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="csc">CSC (NFC-e)</Label>
              <Input
                id="csc" type="password" autoComplete="off" value={form.cscNfce ?? ''}
                placeholder={data.cscConfigurado ? 'Já configurado — preencha só para trocar' : 'Código de segurança'}
                onChange={(e) => setForm((f) => ({ ...f, cscNfce: e.target.value || null }))}
              />
            </div>
          </div>

          <div className="flex justify-end">
            <Button
              onClick={() => salvar({ ...form, percCreditoIcmsSn: form.crt === 3 ? 0 : form.percCreditoIcmsSn })}
              disabled={bloqueado || isSalvando} className="gap-2"
            >
              <Save className="h-4 w-4" /> {isSalvando ? 'Salvando…' : 'Salvar dados de emissão'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <FileKey className="h-4 w-4 text-primary" /> Certificado digital A1
            {!certificado.instalado && <Badge variant="outline">Não instalado</Badge>}
            {certificado.instalado && certificado.vencido && <Badge variant="destructive">Vencido</Badge>}
            {certificado.instalado && !certificado.vencido && (
              <Badge variant="secondary">
                Válido até {certificado.validade ? new Date(certificado.validade).toLocaleDateString('pt-BR') : '—'}
              </Badge>
            )}
          </CardTitle>
          <CardDescription>
            Arquivo .pfx do e-CNPJ da empresa. Fica guardado criptografado e é usado para assinar as notas.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {certificado.instalado && !certificado.vencido
            && certificado.diasParaVencer !== null && certificado.diasParaVencer <= 30 && (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Certificado perto de vencer</AlertTitle>
              <AlertDescription>
                Faltam {certificado.diasParaVencer} dia(s). Renove e envie o novo arquivo para não parar a emissão.
              </AlertDescription>
            </Alert>
          )}

          <div className="grid gap-4 sm:grid-cols-[2fr_1fr_auto] sm:items-end">
            <div className="space-y-2">
              <Label htmlFor="pfx">Arquivo do certificado (.pfx)</Label>
              <Input
                id="pfx" ref={inputArquivo} type="file" accept=".pfx,.p12"
                onChange={(e) => setArquivo(e.target.files?.[0] ?? null)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="senha-pfx">Senha</Label>
              <Input
                id="senha-pfx" type="password" autoComplete="new-password" value={senha}
                onChange={(e) => setSenha(e.target.value)}
              />
            </div>
            <Button
              variant="outline" className="gap-2"
              disabled={!data.cadastrado || !arquivo || !senha || isEnviandoCertificado}
              onClick={enviar}
            >
              <Upload className="h-4 w-4" /> {isEnviandoCertificado ? 'Enviando…' : 'Enviar certificado'}
            </Button>
          </div>
          {!data.cadastrado && (
            <p className="text-xs text-muted-foreground">Salve os dados de emissão antes de enviar o certificado.</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-primary" /> Teste de comunicação
          </CardTitle>
          <CardDescription>
            Consulta o status do serviço da SEFAZ com o certificado e o ambiente configurados. Não emite nota.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center gap-4">
          <Button
            variant="outline" className="gap-2"
            disabled={!data.cadastrado || !certificado.instalado || isTestandoSefaz}
            onClick={() => testarSefaz()}
          >
            <Radio className="h-4 w-4" /> {isTestandoSefaz ? 'Consultando…' : 'Testar SEFAZ'}
          </Button>
          {statusSefaz && (
            <span className="flex items-center gap-2 text-sm">
              {statusSefaz.emOperacao
                ? <CheckCircle2 className="h-4 w-4 text-green-600" />
                : <AlertTriangle className="h-4 w-4 text-destructive" />}
              {statusSefaz.cStat} — {statusSefaz.xMotivo}
            </span>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
